// @djsem/ui — self-contained inline SVG icon set (no external dependency).
//
// 16px stroke icons on a 24-unit viewBox, currentColor stroke so they inherit text colour and
// hover/active states for free. One icon per nav module id, plus a few UI glyphs. Kept
// deliberately minimal and geometric to match the editorial, restrained visual language.

import { createElement, type ReactNode } from "react";

function svg(children: ReactNode, extra?: { fill?: boolean }) {
  return createElement(
    "svg",
    {
      width: 16, height: 16, viewBox: "0 0 24 24",
      fill: extra?.fill ? "currentColor" : "none",
      stroke: extra?.fill ? "none" : "currentColor",
      strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round",
      "aria-hidden": true, focusable: false,
    },
    children,
  );
}
const p = (d: string) => createElement("path", { d, key: d });
const c = (cx: number, cy: number, r: number) => createElement("circle", { cx, cy, r, key: `${cx}-${cy}-${r}` });
const l = (x1: number, y1: number, x2: number, y2: number) => createElement("line", { x1, y1, x2, y2, key: `${x1}${y1}${x2}${y2}` });
const rect = (x: number, y: number, w: number, h: number, rx = 1.5) => createElement("rect", { x, y, width: w, height: h, rx, key: `r${x}${y}` });

/** Nav-module icons, keyed by DJSEMNavModuleId. */
export const NAV_ICONS: Record<string, ReactNode> = {
  // Dashboard — a tidy 2x2 tile grid.
  dashboard: svg([rect(3, 3, 7, 7), rect(14, 3, 7, 7), rect(3, 14, 7, 7), rect(14, 14, 7, 7)]),
  // Library — stacked records / discs.
  library: svg([c(12, 12, 8), c(12, 12, 2.2)]),
  // Playlists — list with a play triangle.
  playlists: svg([l(4, 7, 15, 7), l(4, 12, 15, 12), l(4, 17, 11, 17), p("M17 14l4 2.5-4 2.5z")]),
  // Crates — a box / crate.
  crates: svg([p("M3 8l9-4 9 4-9 4z"), p("M3 8v8l9 4 9-4V8"), l(12, 12, 12, 20)]),
  // Sets — layered waveform bars building up.
  sets: svg([l(5, 14, 5, 17), l(9, 10, 9, 17), l(13, 6, 13, 17), l(17, 11, 17, 17), l(21, 8, 21, 17)]),
  // Visuals — an eye / scene.
  visuals: svg([p("M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z"), c(12, 12, 2.5)]),
  // Brand — a diamond / gem identity mark.
  brand: svg([p("M12 3l7 6-7 12-7-12z"), l(5, 9, 19, 9)]),
  // Create — a spark / plus star.
  create: svg([p("M12 4v16"), p("M4 12h16"), p("M6.5 6.5l11 11"), p("M17.5 6.5l-11 11")]),
  // Assets — layered images.
  assets: svg([rect(3, 6, 14, 12), p("M7 3h14v14"), c(8, 11, 1.5), p("M4 16l4-3 4 3")]),
  // Releases — a rocket-less disc-with-ring (release).
  releases: svg([c(12, 12, 8), p("M12 4v4"), p("M12 16v4"), p("M4 12h4"), p("M16 12h4")]),
  // Campaigns — a megaphone.
  campaigns: svg([p("M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1z"), p("M15 8a5 5 0 010 8")]),
  // Gigs — a calendar with a pin.
  gigs: svg([rect(3, 5, 18, 16, 2), l(3, 9, 21, 9), l(8, 3, 8, 6), l(16, 3, 16, 6), c(12, 15, 2)]),
  // Live — a broadcast / radiating signal.
  live: svg([c(12, 12, 2.5), p("M6.5 6.5a8 8 0 000 11"), p("M17.5 6.5a8 8 0 010 11"), p("M4 4a12 12 0 000 16"), p("M20 4a12 12 0 010 16")]),
  // Set Learning — a rising evidence/retention curve.
  feedback: svg([p("M3 17l5-6 4 3 6-8 3 3"), l(3, 21, 21, 21)]),
  // Jobs — a spinner / gear-lite.
  jobs: svg([c(12, 12, 3), p("M12 4v2"), p("M12 18v2"), p("M4 12h2"), p("M18 12h2"), p("M6 6l1.5 1.5"), p("M16.5 16.5L18 18"), p("M18 6l-1.5 1.5"), p("M7.5 16.5L6 18")]),
  // Marketplace — a shop / storefront.
  marketplace: svg([p("M4 9h16l-1 11H5z"), p("M4 9l2-5h12l2 5"), l(9, 13, 9, 20), l(15, 13, 15, 20)]),
  // Settings — sliders.
  settings: svg([l(4, 8, 20, 8), l(4, 16, 20, 16), c(9, 8, 2), c(15, 16, 2)]),
  // Distribution — outward arrows / share.
  distribution: svg([c(6, 12, 2.5), c(18, 6, 2.5), c(18, 18, 2.5), l(8.2, 10.8, 15.8, 7.2), l(8.2, 13.2, 15.8, 16.8)]),
  // Gated drops — a lock.
  drops: svg([rect(5, 11, 14, 9, 2), p("M8 11V8a4 4 0 018 0v3"), c(12, 15.5, 1)]),
  // Admin — a shield.
  admin: svg([p("M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"), p("M9 12l2 2 4-4")]),
  support: svg([c(12, 12, 9), p("M9.5 9.5a2.5 2.5 0 013.5-.5c1 .8 1 2 0 2.8-.6.5-1 1-1 1.7"), l(12, 17, 12, 17.2)]),
  moderation: svg([p("M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"), l(12, 9, 12, 13), l(12, 15.5, 12, 15.7)]),
};

/** UI glyph icons for the top bar (clearer than the old ◔ / ☰ unicode). */
export const UI_ICONS = {
  bell: svg([p("M6 9a6 6 0 0112 0c0 5 2 6 2 6H4s2-1 2-6z"), p("M10 20a2 2 0 004 0")]),
  inbox: svg([p("M3 12h5l2 3h4l2-3h5"), p("M5 5h14l2 7v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z")]),
  search: svg([c(11, 11, 7), l(21, 21, 16.5, 16.5)]),
  chevron: svg([p("M6 9l6 6 6-6")]),
};

/** Fallback dot for any module without a dedicated icon. */
export const FALLBACK_ICON: ReactNode = svg([c(12, 12, 3)], { fill: true });
